import React from 'react'
import styled from 'styled-components'
import { FormFieldDefinition, SelectOption } from '../../types/form.types'
import { SearchableSelect } from '../../../ui/SearchableSelect/SearchableSelect'
import IconTextField from '../../../ui/Input/IconTextField'
import ToggleIOS from '../../../ui/Toggles/ToggleIOS'

type Props<T> = {
  field: FormFieldDefinition<T>
  value: unknown
  error?: string
  onChange: (name: keyof T, value: unknown) => void
  disabled?: boolean
}

const FieldRenderer = <T,>({
  field,
  value,
  error,
  onChange,
  disabled,
}: Props<T>) => {
  const isDisabled = disabled || field.disabled
  const hasError = !!error
  const id = `field-${String(field.name)}`

  // Inline toggle card (label sits next to the switch — no FormField wrapper).
  if (field.inputType === 'boolean-select' || field.inputType === 'checkbox') {
    return (
      <ToggleCard $hasError={hasError}>
        <ToggleLabel id={`${id}-label`}>
          {field.label}
          {field.required && <RequiredMark aria-hidden="true"> *</RequiredMark>}
        </ToggleLabel>
        <ToggleIOS
          index={id}
          checked={!!value}
          onChange={(next) => onChange(field.name, next)}
          disabled={isDisabled}
        />
      </ToggleCard>
    )
  }

  if (field.inputType === 'select' || field.inputType === 'searchable-select') {
    const options: SelectOption[] = field.getOptions?.() ?? field.options ?? []
    const selected = options.find((o) => o.value === value) ?? null
    return (
      <Control>
        <SearchableSelect<SelectOption>
          items={options}
          value={selected}
          onSelect={(opt) => onChange(field.name, opt ? opt.value : null)}
          getKey={(o) => o.value}
          getLabel={(o) => o.label}
          placeholder={field.placeholder}
          disabled={isDisabled}
        />
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Control>
    )
  }

  if (field.inputType === 'textarea') {
    return (
      <Control>
        <TextArea
          id={id}
          value={String(value ?? '')}
          disabled={isDisabled}
          $hasError={hasError}
          placeholder={field.placeholder}
          rows={3}
          onChange={(e) => onChange(field.name, e.target.value)}
          aria-label={field.label}
          aria-invalid={hasError}
          aria-required={field.required}
        />
        {error && <ErrorText role="alert">{error}</ErrorText>}
      </Control>
    )
  }

  const isNumber = field.inputType === 'number'
  return (
    <Control>
      <IconTextField
        id={id}
        type={isNumber ? 'number' : field.inputType === 'date' ? 'date' : 'text'}
        inputMode={isNumber ? 'decimal' : undefined}
        value={(value ?? '') as string | number}
        disabled={isDisabled}
        error={hasError}
        placeholder={field.placeholder}
        onChange={(e) =>
          onChange(field.name, isNumber ? Number(e.target.value) : e.target.value)
        }
        aria-label={field.label}
        aria-invalid={hasError}
        aria-required={field.required}
      />
      {error && <ErrorText role="alert">{error}</ErrorText>}
    </Control>
  )
}

export default FieldRenderer

const Control = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
`

const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme }) => theme.colors.danger};
  text-align: start;
`

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.danger};
`

const ToggleCard = styled.div<{ $hasError?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.65rem 0.85rem;
  background: ${({ theme }) => theme.colors.bgLayout};
  border: 1px solid
    ${({ theme, $hasError }) => ($hasError ? theme.colors.danger : theme.colors.line)};
  border-radius: ${({ theme }) => theme.radii.md};
  transition: border-color ${({ theme }) => theme.motion.fast} ease;
`

const ToggleLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink2};
  flex: 1;
  min-width: 0;
  text-align: start;
`

const TextArea = styled.textarea<{ $hasError?: boolean }>`
  width: 100%;
  min-height: 5rem;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid
    ${({ theme, $hasError }) => ($hasError ? theme.colors.danger : theme.colors.inputBorder)};
  border-radius: 0.875rem;
  padding: 0.75rem 0.875rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  outline: none;
  resize: vertical;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.line2};
    cursor: not-allowed;
    opacity: 0.7;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`
